import React from "react";
import i18n from "../../../i18n";
import { fs, T } from "./tokens";

// Loading states for Life Areas. They mirror the real layout (header, three
// rings, three groups of rows) so nothing jumps when the data arrives.
// Keyframes (lg2-shimmer / lg2-spin / lg2-load-pulse / lg2-breathe) live in the
// card's scoped CSS and are switched off under prefers-reduced-motion.

const shimmer = () => `linear-gradient(90deg, ${T.color.bg.overlay} 25%, #F2F1FC 50%, ${T.color.bg.overlay} 75%)`;

export const Skel: React.FC<{
  w?: number | string;
  h?: number | string;
  r?: number;
  style?: React.CSSProperties;
}> = ({ w = "100%", h = 14, r = T.radius.md, style }) => (
  <div
    aria-hidden="true"
    style={{
      width: w,
      height: h,
      borderRadius: r,
      flexShrink: 0,
      background: shimmer(),
      backgroundSize: "200% 100%",
      animation: "lg2-shimmer 1.4s linear infinite",
      ...style,
    }}
  />
);

const RowSkel: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "10px 14px",
      background: T.color.bg.card,
      border: `1px solid ${T.color.bg.overlay}`,
      borderRadius: T.radius.xl,
    }}
  >
    <Skel w={36} h={36} r={T.radius.full} />
    <div style={{ flex: 1, display: "grid", gap: 7 }}>
      <Skel w="36%" h={12} />
      <Skel w="22%" h={10} />
    </div>
    <Skel w={84} h={6} r={T.radius.full} />
    <Skel w={30} h={18} />
  </div>
);

// Same geometry as the real chart (viewBox 600, rings at r120 / r180 / r240).
const RINGS = [
  { r: 120, color: T.color.rings.inner, frac: 0.3, secs: 1.8, reverse: false },
  { r: 180, color: T.color.rings.middle, frac: 0.22, secs: 2.6, reverse: true },
  { r: 240, color: T.color.rings.outer, frac: 0.16, secs: 3.4, reverse: false },
];

const RingsSkeleton: React.FC = () => (
  <div style={{ width: 440, maxWidth: "100%", aspectRatio: "1 / 1", margin: "0 auto" }} aria-hidden="true">
    <svg viewBox="0 0 600 600" width="100%" height="100%">
      <defs>
        <radialGradient id="lg2-skel-core" cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor={T.color.primaryButton} />
          <stop offset="100%" stopColor={T.color.secondary} />
        </radialGradient>
      </defs>
      {RINGS.map((ring) => {
        const circ = 2 * Math.PI * ring.r;
        return (
          <g key={ring.r}>
            <circle cx={300} cy={300} r={ring.r} fill="none" stroke={T.color.bg.overlay} strokeOpacity={0.8} strokeWidth={36} />
            <circle
              cx={300}
              cy={300}
              r={ring.r}
              fill="none"
              stroke={ring.color}
              strokeOpacity={0.75}
              strokeWidth={36}
              strokeLinecap="round"
              strokeDasharray={`${circ * ring.frac} ${circ}`}
              style={{
                transformOrigin: "center",
                transformBox: "fill-box",
                animation: `lg2-spin ${ring.secs}s linear infinite ${ring.reverse ? "reverse" : "normal"}`,
              }}
            />
          </g>
        );
      })}
      <g style={{ transformOrigin: "center", transformBox: "fill-box", animation: "lg2-load-pulse 1.6s ease-in-out infinite" }}>
        <circle cx={300} cy={300} r={60} fill="url(#lg2-skel-core)" />
      </g>
    </svg>
  </div>
);

export const LifeAreasSkeleton: React.FC = () => (
  <div role="status" aria-busy="true" aria-label={i18n.t("lifeAreas.loading")}>
    <header aria-hidden="true" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div style={{ display: "grid", gap: 9 }}>
        <Skel w={190} h={24} />
        <Skel w={270} h={12} />
      </div>
      <Skel w={38} h={38} r={T.radius.full} />
    </header>

    <div style={{ marginTop: 16 }}>
      <RingsSkeleton />
    </div>

    <p
      style={{
        ...fs("sm", 500),
        textAlign: "center",
        margin: "4px 0 0",
        color: T.color.text.secondary,
        animation: "lg2-breathe 1.8s ease-in-out infinite",
      }}
    >
      Charting your Life Areas…
    </p>

    <div aria-hidden="true" style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
      <Skel w={160} h={26} r={T.radius.full} />
      <Skel w={54} h={26} r={T.radius.full} />
    </div>

    {[3, 3, 2].map((rows, g) => (
      <div key={g} aria-hidden="true" style={{ marginTop: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div style={{ display: "grid", gap: 7 }}>
            <Skel w={130} h={14} />
            <Skel w={230} h={10} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Skel w={44} h={20} r={T.radius.full} />
            <Skel w={30} h={20} />
          </div>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {Array.from({ length: rows }, (_, i) => (
            <RowSkel key={i} />
          ))}
        </div>
      </div>
    ))}

    <div aria-hidden="true" style={{ marginTop: 24 }}>
      <Skel w={96} h={14} style={{ marginBottom: 10 }} />
      <Skel h={52} r={T.radius.xl} />
      <Skel h={52} r={T.radius.xl} style={{ marginTop: 8 }} />
      <Skel h={48} r={T.radius.lg} style={{ marginTop: 24 }} />
    </div>
  </div>
);

// Settings dialog while the settings load.
export const SettingsSkeleton: React.FC = () => (
  <div role="status" aria-busy="true" aria-label={i18n.t("lifeAreas.loadingSettings")} aria-hidden={false}>
    <div aria-hidden="true">
      <Skel w="70%" h={12} style={{ marginBottom: 12 }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {Array.from({ length: 8 }, (_, i) => (
          <Skel key={i} h={44} r={T.radius.lg} />
        ))}
      </div>
      <Skel w="30%" h={12} style={{ margin: "18px 0 8px" }} />
      <Skel h={38} r={T.radius.lg} />
    </div>
  </div>
);
